import Vue from "vue";
import Owner from "@/model/cospirit-api/Owner";
import TopicRepository from "@/repositories/TopicRepository";
import _ from "lodash";

export interface Wish {
    uuid: string;
    label: string;
}
export interface Topic {
    uuid: string;
    title: string;
    archivedAt: string|null;
    owner: Owner;
    scopeOwner: Owner;
    parent: Topic|null;
    children: Topic[];
    wishes: Wish[];
    minAmount: number;
}

export default {
    state: {
        topics: [],
    },
    mutations: {
        setTopics: (state: any, topics: Topic[]) => {
            Vue.set(state, "topics", topics);
        },
        removeTopic: (state: any, topic: Topic) => {
            const index = state.topics.findIndex((topicData: Topic) => topicData.uuid === topic.uuid);
            state.topics.splice(index, 1);
        },
    },
    getters: {
        getAllTopics: (state: any): Topic[] => {
            return state.topics;
        },
        getTopics: (state: any) => (parentUuid: string | null): Topic[] => {
            return _.filter(state.topics, (topic: Topic) => {
                return _.get(topic, "parent.uuid", null) === parentUuid;
            });
        },
        getTopicScopeOwners: (state: any): string[] => {
            return _.reduce(state.topics, (result: string[], topic: Topic) => {
                if (!_.has(topic, "scopeOwner.uuid") || _.get(topic, "archivedAt", null)) {
                    return result;
                }

                const alreadyExists = _.some(
                    result,
                    (scopeOwnerUuid) => scopeOwnerUuid === _.get(topic, "scopeOwner.uuid")
                );
                if (!alreadyExists) {
                    result.push(topic.scopeOwner.uuid);
                }

                return result;
            }, []);
        },
        getFilteredTopics: (state: any, getters: any) => (
            parentUuid: string | null,
            scopeOwnerUuid: string,
            withTopics: string[] = [],
            allTopics: boolean = false
        ): Topic[] => {
            const topics = allTopics ? getters.getAllTopics : getters.getTopics(parentUuid);
            const filteredTopics = _.filter(topics, (topic: Topic) => {
                return (_.get(topic, "scopeOwner.uuid", null) === scopeOwnerUuid
                        && !_.get(topic, "archivedAt", null)
                    ) || _.some(withTopics, topic.uuid);
            });

            const haveTopicsInScope = _.some(filteredTopics, (topic: Topic) => {
                return _.get(topic, "scopeOwner.uuid", null) === scopeOwnerUuid;
            });
            if (!haveTopicsInScope) {
                // On récupère les topics liés dans le scope du plus proche parent
                const scopeOwner = _.find(
                    getters.getMasterOwners,
                    (owner: Owner) => {
                        return owner.uuid === scopeOwnerUuid;
                    }
                );
                const parentScopeOwnerUuid = _.get(scopeOwner, "parent.uuid", null);
                if (null === parentScopeOwnerUuid) {
                    return [];
                }

                return getters.getFilteredTopics(parentUuid, parentScopeOwnerUuid, withTopics, allTopics);
            }

            return filteredTopics;
        },
    },
    actions: {
        loadTopics: ({ commit }: any) => {
            commit("setTopics", []);

            return new Promise((resolve: (value: object) => void, reject: (value: object) => void) => {
                TopicRepository.get().then((data: any) => {
                    commit("setTopics", data);
                    resolve(data);
                }).catch((reason: any) => {
                    reject(reason);
                });
            });
        },
        saveTopic: ({ dispatch }: any, topic: Topic) => {
            return new Promise((resolve: (value: object) => void, reject: (value: object) => void) => {
                TopicRepository.save(topic).then((data: any) => {
                    dispatch("loadTopics").then(() => {
                        resolve(data);
                    });
                }).catch((reason: any) => {
                    reject(reason);
                });
            });
        },
        deleteTopic: ({ commit }: any, topic: object) => {
            return new Promise((resolve, reject) => {
                TopicRepository.delete(topic)
                .then((data) => {
                        commit("removeTopic", topic);
                        resolve(data);
                    })
                    .catch((reason) => {
                        reject(reason);
                    });
            });
        },
    },
};
